import Echeance from "./Echeance";

/**
 * L'échéancier est généré exclusivement côté backend (procédures stockées)
 * à chaque étape du workflow : simulation, demande, proposition, validation,
 * modification. Le frontend ne fait que consulter le résultat persisté.
 * Les montants sont des nombres bruts et les dates sont en ISO (yyyy-mm-dd).
 */
export interface EcheancierTotaux {
    totAmortissement: number;
    totInteret: number;
    totTaxe: number;
    totEpargne: number;
    totAnnuiteHT: number;
    totAnnuiteTTC: number;
}

export interface EcheancierPretResult {
    echeances: Echeance[];
    totaux: EcheancierTotaux;
    /** Version consultee (derniere version si non precisee a l'appel). */
    version: number;
    /** Derniere version generee (= version actuelle). */
    versionCourante: number;
    /** true si la version consultee est la version actuelle. */
    isCourant: boolean;
}

/** Resume d'une version d'echeancier (liste des versions). */
export interface EcheancierVersion {
    version: number;
    /** 1 = version actuelle (derniere generee). */
    isCourant: number;
    nbLignes: number;
    montant: number;
    tauxInt: number;
    nbEcheances: number;
    nbPerDif: number;
    dat1Eche: string;
    utilGen: string;
    datGen: string;
}

/** Ligne d'echeance rattachee a une version. */
export interface EcheancierVersionLigne extends Echeance {
    version: number;
}

/** Totaux d'une version d'echeancier. */
export interface EcheancierVersionTotaux extends EcheancierTotaux {
    version: number;
}

/** Toutes les versions d'un echeancier : resumes + lignes + totaux. */
export interface EcheancierVersionsResult {
    versions: EcheancierVersion[];
    lignes: EcheancierVersionLigne[];
    totaux: EcheancierVersionTotaux[];
}

export function toNumber(value: any): number {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number") return value;
    const parsed = parseFloat(String(value).replace(/\s/g, "").replace(",", "."));
    return isNaN(parsed) ? 0 : parsed;
}

export function toIsoDate(value: any): string {
    if (!value) return "";
    if (value instanceof Date) return value.toISOString().substr(0, 10);
    let date = String(value).replaceAll("/", "-");
    // JJ-MM-AAAA -> AAAA-MM-JJ
    if (date.substr(0, 10).split("-")[0].length === 2) {
        const parts = date.substr(0, 10).split("-");
        date = [parts[2], parts[1], parts[0]].join("-");
    }
    return date.substr(0, 10);
}

export function normalizeEcheance(raw: any, index: number): Echeance {
    return {
        numSeq: toNumber(raw?.numSeq ?? raw?.NUMSEQ ?? raw?.numseq ?? index + 1),
        dateEcheacnce: toIsoDate(raw?.dateEcheacnce ?? raw?.dateEcheance ?? raw?.DATECH ?? raw?.DATECHE ?? ""),
        capital: toNumber(raw?.capital ?? raw?.MTNCRD ?? raw?.mtnCrd),
        amortissement: toNumber(raw?.amortissement ?? raw?.AMMORT ?? raw?.ammort),
        interet: toNumber(raw?.interet ?? raw?.INTERE ?? raw?.intere),
        taxe: toNumber(raw?.taxe ?? raw?.monTaxe ?? raw?.MONTAXE),
        epargne: toNumber(raw?.epargne ?? raw?.MONTEPARG ?? raw?.montEparg),
        anHT: toNumber(raw?.anHT ?? raw?.annuiteHT ?? raw?.ANNUIT),
        anTTC: toNumber(raw?.anTTC ?? raw?.annuiteTTC ?? raw?.annuite ?? 0)
    };
}

/**
 * Normalise la réponse backend de consultation de l'échéancier persisté.
 * Accepte { echeances, totaux }, { Table: [...] } ou un tableau brut.
 * Si les totaux sont absents ils sont recalculés pour l'affichage uniquement.
 */
export function normalizeEcheancierResult(raw: any): EcheancierPretResult {
    const lignes: any[] = raw?.echeances ?? raw?.Echeances ?? raw?.Table ?? (Array.isArray(raw) ? raw : []);
    const echeances: Echeance[] = (lignes || []).map((ligne: any, index: number) => normalizeEcheance(ligne, index));

    const t = raw?.totaux ?? raw?.Totaux;
    const totaux: EcheancierTotaux = {
        totAmortissement: toNumber(t?.totAmortissement ?? t?.TotAm ?? t?.totalAmortissement ?? echeances.reduce((s, e) => s + e.amortissement, 0)),
        totInteret: toNumber(t?.totInteret ?? t?.TotIt ?? t?.totalInteret ?? echeances.reduce((s, e) => s + e.interet, 0)),
        totTaxe: toNumber(t?.totTaxe ?? t?.TotTaxe ?? t?.totalTaxe ?? echeances.reduce((s, e) => s + e.taxe, 0)),
        totEpargne: toNumber(t?.totEpargne ?? t?.TotEpargn ?? t?.totalEpargne ?? echeances.reduce((s, e) => s + e.epargne, 0)),
        totAnnuiteHT: toNumber(t?.totAnnuiteHT ?? t?.TotAnnuite ?? t?.totalAnnuiteHT ?? echeances.reduce((s, e) => s + e.anHT, 0)),
        totAnnuiteTTC: toNumber(t?.totAnnuiteTTC ?? t?.TotAnTTC ?? t?.totalAnnuiteTTC ?? echeances.reduce((s, e) => s + e.anTTC, 0))
    };

    return {
        echeances,
        totaux,
        version: toNumber(raw?.version ?? raw?.VERSION ?? 0),
        versionCourante: toNumber(raw?.versionCourante ?? raw?.VERSIONCOURANTE ?? 0),
        isCourant: toNumber(raw?.isCourant ?? raw?.ISCOURANT ?? 0) === 1
    };
}

/** Normalise la liste des versions d'un echeancier (resumes + lignes + totaux). */
export function normalizeEcheancierVersions(raw: any): EcheancierVersionsResult {
    const versionsRaw: any[] = Array.isArray(raw) ? raw : (raw?.versions ?? raw?.Versions ?? []);
    const versions: EcheancierVersion[] = (versionsRaw || []).map((v: any) => ({
        version: toNumber(v?.version ?? v?.VERSION),
        isCourant: toNumber(v?.isCourant ?? v?.ISCOURANT),
        nbLignes: toNumber(v?.nbLignes ?? v?.NBLIGNES),
        montant: toNumber(v?.montant ?? v?.MONTAN),
        tauxInt: toNumber(v?.tauxInt ?? v?.TAUXINT),
        nbEcheances: toNumber(v?.nbEcheances ?? v?.NBRECHE),
        nbPerDif: toNumber(v?.nbPerDif ?? v?.NBPERDIF),
        dat1Eche: String(v?.dat1Eche ?? v?.DAT1ECHE ?? ''),
        utilGen: String(v?.utilGen ?? v?.UTILGEN ?? ''),
        datGen: String(v?.datGen ?? v?.DATGEN ?? '')
    }));

    const lignesRaw: any[] = raw?.lignes ?? raw?.Lignes ?? [];
    const lignes: EcheancierVersionLigne[] = (lignesRaw || []).map((l: any, i: number) => ({
        ...normalizeEcheance(l, i),
        version: toNumber(l?.version ?? l?.VERSION)
    }));

    const totauxRaw: any[] = raw?.totaux ?? raw?.Totaux ?? [];
    const totaux: EcheancierVersionTotaux[] = (totauxRaw || []).map((t: any) => ({
        version: toNumber(t?.version ?? t?.VERSION),
        totAmortissement: toNumber(t?.totAmortissement ?? t?.TOTAMORTISSEMENT),
        totInteret: toNumber(t?.totInteret ?? t?.TOTINTERET),
        totTaxe: toNumber(t?.totTaxe ?? t?.TOTTAXE),
        totEpargne: toNumber(t?.totEpargne ?? t?.TOTEPARGNE),
        totAnnuiteHT: toNumber(t?.totAnnuiteHT ?? t?.TOTANNUITEHT),
        totAnnuiteTTC: toNumber(t?.totAnnuiteTTC ?? t?.TOTANNUITETTC)
    }));

    return { versions, lignes, totaux };
}
